import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  ModalProps,
  RawInput,
} from '@payaca/ambrosia';
import { FC, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import useCreateTeam from '@/api/mutations/teams/useCreateTeam';
import { CreateTeamMutation } from '@/gql/graphql';

// `.trim()` transforms the value, so a whitespace-only name fails `min(1)` and
// the submitted name is always trimmed.
const teamFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(50, 'Name must be 50 characters or less'),
});

type TFormState = z.infer<typeof teamFormSchema>;

type Props = {
  onSuccess?: (team: CreateTeamMutation['createTeam']) => void;
} & Pick<ModalProps, 'isOpen' | 'onClose'>;

const CreateTeamBody: FC<Omit<Props, 'isOpen'>> = ({
  onClose,
  onSuccess,
}): JSX.Element => {
  const { mutate: createTeam, isPending } = useCreateTeam({
    onSuccess: (data) => onSuccess?.(data.createTeam),
  });

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: { name: '' },
  });

  const onSubmit = (state: TFormState) => {
    createTeam({ name: state.name });
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <Fieldset>
          <Field name="name">
            <Field.Label>Team name</Field.Label>
            <RawInput {...formMethods.register('name')} autoComplete="off" />
          </Field>
        </Fieldset>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button onClick={onClose} variant="secondary" type="button">
            Cancel
          </Button>
          <Button type="submit" isProcessing={isPending} disabled={isPending}>
            Create team
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

const NewTeamModal: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
}): JSX.Element => {
  return (
    <Modal isOpen={isOpen} title="Create team" onClose={onClose} size="sm">
      {/* Remount the form on open so its defaults reset each time. */}
      {isOpen && <CreateTeamBody onClose={onClose} onSuccess={onSuccess} />}
    </Modal>
  );
};

export default NewTeamModal;
